import { HeaderNavMenu, WebViewScreen } from '@diritto/common/app/webview-header.constant';
import { Color } from '@diritto/common/style/color';
import { css } from '@emotion/react';
import styled from '@emotion/styled';
import { LoginView } from 'app-components/accounts/LoginView';
import { HeaderNavBarHeight, isPC } from 'constants/layout';
import { ZIndex } from 'constants/zIndex';
import { ExclamationIcon2 } from 'icons/ExclamationIcon2';
import { DirittoAppBridge } from 'libs/DirittoAppBridge';
import { useRouter } from 'next/navigation';
import React, { ReactNode, useCallback, useMemo } from 'react';
import { Divider } from 'ui-components/common/Divider';
import { SafeAreaBottomStyles, ViewPortHeight } from 'ui-components/layout/styles';
import { GlobalNavigationBar } from 'ui-components/navigations/GlobalNavigationBar';
import { BackButton } from 'ui-components/navigations/HeaderButtons';
import { HeaderNavigationBar } from 'ui-components/navigations/HeaderNavigationBar';
import { useClient } from 'ui-hooks/useClient';
import { useInAppHeader } from 'ui-hooks/useInAppHeader';
import { useInAppStatusBar } from 'ui-hooks/useInAppStatusBar';
import { useViewPort } from 'ui-hooks/useViewPort';

interface LoginLayoutProps {
  children?: ReactNode;
  title: string;
  description?: string;
  Icon?: typeof ExclamationIcon2;
  header?: boolean;
  onClickBack?: () => void;
}

export const LoginLayout = ({
  children,
  title,
  Icon,
  onClickBack,
  description = '디리토에 오신 것을 환영해요!',
  header = false,
}: LoginLayoutProps) => {
  const router = useRouter();
  const client = useClient();
  const showWebHeader = header && !client.isApp;

  const handleBack = useCallback(
    () => (client.isWeb ? router.back() : DirittoAppBridge.closeSignView()),
    [client.isWeb, router],
  );

  const leftMenuListApp = useMemo(() => [HeaderNavMenu.Back], []);
  const leftMenuListWeb = useMemo(
    () => [<BackButton key="back" onClick={onClickBack || handleBack} />],
    [handleBack, onClickBack],
  );

  useInAppHeader({ screen: WebViewScreen.Sign, title: '', hideHeader: !header, leftMenuList: leftMenuListApp });
  useInAppStatusBar(WebViewScreen.Sign);

  const { height: viewportHeight } = useViewPort();
  return (
    <StyledWrapper height={viewportHeight}>
      <StyledGnbContainer>
        <GlobalNavigationBar />
        <Divider />
      </StyledGnbContainer>
      <StyledContainer>
        {showWebHeader && (
          <LoginHeaderWrapper>
            <HeaderNavigationBar leftButtons={leftMenuListWeb} />
          </LoginHeaderWrapper>
        )}
        <LoginView title={title} description={description} Icon={Icon}>
          {children}
        </LoginView>
      </StyledContainer>
    </StyledWrapper>
  );
};

const StyledWrapper = styled.div(
  ({ height }: { height: number }) => css`
    display: flex;
    flex-direction: column;
    width: 100%;
    ${ViewPortHeight(height)};
  `,
);

const StyledGnbContainer = styled.nav`
  position: relative;
  background-color: ${Color.White};
  width: 100%;
  display: none;

  @media (${isPC}) {
    display: block;
  }
`;

const StyledContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  flex: 1;

  ${SafeAreaBottomStyles()};

  @media (${isPC}) {
    margin: auto;
  }
`;

const LoginHeaderWrapper = styled.nav`
  position: relative;
  background-color: ${Color.White};
  width: 100%;
  height: ${HeaderNavBarHeight}px;
  z-index: ${ZIndex.NavBar};
  border-bottom: 1px solid ${Color.Gray300};

  @media (${isPC}) {
    display: none;
  }
`;
